import React, { useEffect, useState } from 'react';
import { Box, IconButton, Modal, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CasinoIcon from '@mui/icons-material/Casino';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { isMobile } from 'react-device-detect';
import { IUserState } from '../../Core/State/IUserState';
import { COLORS, type HistoryTab } from './HistoryConstants';
import CashHistoryTab from './CashHistoryTab';
import TournamentHistoryTab from './TournamentHistoryTab';

// ─────────────────────────────────────────────────────────────────────────────
// Tab button (pill style)
// ─────────────────────────────────────────────────────────────────────────────

const TabButton: React.FC<{
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  color: string;
  children: React.ReactNode;
}> = ({ active, onClick, icon, color, children }) => (
  <Box
    component="button"
    onClick={onClick}
    sx={{
      cursor: 'pointer',
      background: active ? `${color}1A` : 'transparent',
      border: active ? `1px solid ${color}44` : '1px solid transparent',
      outline: 'none',
      borderRadius: '8px',
      px: 2,
      py: 0.75,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.75,
      fontSize: '13px',
      fontWeight: active ? 700 : 500,
      fontFamily: 'Inter, sans-serif',
      color: active ? color : COLORS.textDisabled,
      transition: 'all 0.2s ease',
      '&:hover': {
        background: active ? `${color}1A` : 'rgba(255, 255, 255, 0.04)',
        color: active ? color : COLORS.textMuted,
        borderColor: active ? `${color}66` : 'rgba(255, 255, 255, 0.08)',
      },
    }}
  >
    {icon}
    {children}
  </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

interface HistoryModalProps {
  open: boolean;
  handleClose: () => void;
  userState: IUserState;
}

const HistoryModal: React.FC<HistoryModalProps> = ({ open, handleClose, userState }) => {
  const [tab, setTab] = useState<HistoryTab>('cash');
  const userId = userState.id;

  useEffect(() => {
    if (!open) setTab('cash');
  }, [open]);

  return (
    <Modal open={open} onClose={handleClose} aria-labelledby="history-modal-title" aria-describedby="history-modal-description">
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: isMobile ? '96vw' : '1040px',
          height: isMobile ? '92vh' : '84vh',
          background: `linear-gradient(180deg, #1e1f27 0%, ${COLORS.modalBg} 40%, #151620 100%)`,
          border: `1px solid ${COLORS.border}`,
          borderRadius: '14px',
          boxShadow:
            '0px 24px 80px rgba(0, 0, 0, 0.75), 0px 0px 1px rgba(255, 255, 255, 0.1), inset 0 0 20px 4px rgba(255, 255, 255, 0.03)',
          p: isMobile ? 2 : 3,
          pt: 0,
          color: COLORS.textPrimary,
          overflow: 'hidden',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Top accent bar */}
        <Box
          sx={{
            height: '3px',
            mx: -3,
            mb: isMobile ? 1.5 : 2,
            flexShrink: 0,
            background: `linear-gradient(90deg, transparent 0%, ${COLORS.primary} 20%, ${COLORS.primary} 80%, transparent 100%)`,
            opacity: 0.7,
          }}
        />

        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5, px: 0.25 }}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                bgcolor: COLORS.tealMuted,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: `1px solid rgba(39, 141, 148, 0.25)`,
              }}
            >
              <ReceiptLongIcon sx={{ fontSize: '20px', color: COLORS.teal }} />
            </Box>
            <Typography
              id="history-modal-title"
              sx={{
                fontWeight: 700,
                fontSize: isMobile ? '20px' : '22px',
                fontFamily: 'Inter, sans-serif',
                letterSpacing: '-0.3px',
              }}
            >
              History
            </Typography>
          </Stack>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            size="small"
            sx={{
              color: COLORS.textDisabled,
              width: 34,
              height: 34,
              borderRadius: '8px',
              border: `1px solid ${COLORS.borderLight}`,
              '&:hover': { color: COLORS.textPrimary, bgcolor: 'rgba(255, 255, 255, 0.06)' },
            }}
          >
            <CloseIcon sx={{ fontSize: '18px' }} />
          </IconButton>
        </Stack>

        {/* Tabs */}
        <Box
          sx={{
            display: 'flex',
            gap: 0.75,
            bgcolor: 'rgba(255, 255, 255, 0.03)',
            borderRadius: '10px',
            p: 0.5,
            mb: 0.75,
          }}
        >
          <TabButton
            active={tab === 'cash'}
            onClick={() => setTab('cash')}
            icon={<CasinoIcon sx={{ fontSize: '16px' }} />}
            color="#d1d1d2"
          >
            Cash
          </TabButton>
          <TabButton
            active={tab === 'mtt'}
            onClick={() => setTab('mtt')}
            icon={<EmojiEventsIcon sx={{ fontSize: '16px' }} />}
            color="#d1d1d2"
          >
            Tournaments
          </TabButton>
        </Box>

        {/* Gradient divider */}
        <Box
          sx={{
            height: '1px',
            background: COLORS.gradientDivider,
            mb: 1.5,
            flexShrink: 0,
          }}
        />

        {/* Tab content */}
        {tab === 'cash' && <CashHistoryTab userId={userId} active={open && tab === 'cash'} />}
        {tab === 'mtt' && <TournamentHistoryTab userId={userId} active={open && tab === 'mtt'} />}
      </Box>
    </Modal>
  );
};

export default HistoryModal;
